'use client';

import { Component, type ErrorInfo, type ReactNode } from 'react';

import {
  hasRecoveryBeenAttempted,
  isChunkLoadError,
  recoverFromStaleChunks,
} from '@/lib/chunk-load-recovery';

type Props = {
  children: ReactNode;
};

type State = {
  hasError: boolean;
  recovering: boolean;
};

/**
 * Error boundary that intercepts webpack chunk-load failures and
 * automatically recovers by purging stale caches & service workers.
 *
 * Only one recovery attempt is made per session to avoid infinite loops.
 * If recovery has already been attempted, a friendly fallback is shown.
 */
export class ChunkLoadBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, recovering: false };
  }

  static getDerivedStateFromError(): Partial<State> {
    return { hasError: true };
  }

  componentDidCatch(error: Error, _info: ErrorInfo): void {
    if (!isChunkLoadError(error)) return;

    if (hasRecoveryBeenAttempted()) {
      // Already tried once this session — give up and show fallback.
      this.setState({ recovering: false });
      return;
    }

    this.setState({ recovering: true });
    recoverFromStaleChunks();
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    if (this.state.recovering) {
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100dvh',
            padding: 24,
            textAlign: 'center',
            fontFamily: 'system-ui, sans-serif',
            color: '#666',
          }}
        >
          <h2 style={{ fontWeight: 600, marginBottom: 8, color: '#222' }}>
            App update detected
          </h2>
          <p>Clearing old cache and reloading…</p>
        </div>
      );
    }

    // Recovery was already attempted but chunks are still stale.
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100dvh',
          padding: 24,
          textAlign: 'center',
          fontFamily: 'system-ui, sans-serif',
          color: '#666',
        }}
      >
        <h2 style={{ fontWeight: 600, marginBottom: 8, color: '#222' }}>
          Something went wrong
        </h2>
        <p style={{ marginBottom: 16 }}>
          A recent update may have caused a conflict. Please clear your browser
          cache and reload.
        </p>
        <button
          onClick={() => recoverFromStaleChunks()}
          style={{
            padding: '10px 24px',
            fontSize: 14,
            fontWeight: 500,
            border: 'none',
            borderRadius: 8,
            background: '#0066ff',
            color: '#fff',
            cursor: 'pointer',
          }}
        >
          Reload app
        </button>
      </div>
    );
  }
}
